import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useAdditionalFields } from "../hooks/addition-fields";
import { useLocale } from "../stores/locale";
import { AdditionalFields } from "./additional-fields";
import { Button } from "./button";
import { Input } from "./input";

export type PersonFormValues = {
  personalIdentificationNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  additionalFields?: Record<string, string>;
};

export function PersonForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: PersonFormValues) => void;
  onSubmit: () => void;
  values: PersonFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const { data: additionalFields } = useAdditionalFields("person");

  const isValid = useMemo(() => {
    return (
      values.personalIdentificationNumber !== "" &&
      additionalFields.every(
        (field) =>
          !field.required ||
          values.additionalFields?.[field.name]?.trim() !== "",
      )
    );
  }, [values, additionalFields]);
  const isDisabled = useMemo(() => {
    return disabled || loading;
  }, [disabled, loading]);

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-2 gap-2">
        <Input
          required
          disabled={isDisabled}
          label={t.person.personalIdentificationNumber}
          type="text"
          value={values.personalIdentificationNumber}
          onChange={(e) =>
            onChange({
              ...values,
              personalIdentificationNumber: e.target.value,
            })
          }
        />
        <Input
          disabled={isDisabled}
          label={t.person.firstName}
          type="text"
          value={values.firstName}
          onChange={(e) => onChange({ ...values, firstName: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.person.lastName}
          type="text"
          value={values.lastName}
          onChange={(e) => onChange({ ...values, lastName: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.person.email}
          type="email"
          value={values.email}
          onChange={(e) => onChange({ ...values, email: e.target.value })}
        />
        <Input
          disabled={isDisabled}
          label={t.person.phone}
          type="tel"
          value={values.phone}
          onChange={(e) => onChange({ ...values, phone: e.target.value })}
        />
        <AdditionalFields
          additionalFields={additionalFields}
          disabled={isDisabled}
          values={values.additionalFields ?? {}}
          onChange={(additionalFieldValues) =>
            onChange({
              ...values,
              additionalFields: {
                ...additionalFieldValues,
              },
            })
          }
        />
      </div>
      <div className="flex gap-2 justify-end">
        {hasError && (
          <p className="text-red-500 flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t.person.error}
          </p>
        )}
        <Button disabled={disabled} severity="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={disabled || !isValid} loading={loading} type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}
